import { useState } from 'react';
import axios from 'axios';
import { Result, Text, Form, Input, Button, Row, Col, Card, Spin, Alert, Space } from '@servahealth/serva-ui-kit';
import { useMutation } from 'react-query';
import { openNotification } from '@servahealth/serva-ui-kit';

const allowedSpecialCharacters = '!@#$%^&*()-_=+/;:|?.,[]{}';
const passwordRequirementErrorMessage = 'Password must meet all listed requirements.';
const requirementMetColor = '#237804';
const requirementMissingColor = '#cf1322';

const passwordRequirements = [
	{
		label: 'At least 8 characters',
		isMet: (value = '') => value.length >= 8,
	},
	{
		label: 'At least 1 uppercase letter',
		isMet: (value = '') => /[A-Z]/.test(value),
	},
	{
		label: 'At least 1 digit',
		isMet: (value = '') => /\d/.test(value),
	},
	{
		label: `At least 1 special character: ${allowedSpecialCharacters.split('').join(' ')}`,
		isMet: (value = '') => allowedSpecialCharacters.split('').some((character) => value.includes(character)),
	},
];

const getPasswordRequirementErrors = (value = '') => (
	passwordRequirements
		.filter(({ isMet }) => !isMet(value))
		.map(({ label }) => label)
);

const getRequirementColor = (password, requirementIsMet) => {
	if (!password) {
		return undefined;
	}

	return requirementIsMet ? requirementMetColor : requirementMissingColor;
};

const LoginReset = (props) => {
	const { resetToken } = props;

	const [password, setPassword] = useState('');
	const [isSuccess, setIsSuccess] = useState(false);

	const onResetSuccess = () => {
		openNotification('success', 'Password saved. You can close this browser tab at any time.');
		setIsSuccess(true);
	};

	const onResetError = (res) => {
		console.log(res);
		openNotification('error', 'There was an issue resetting your password. If you clicked Forgot my Password multiple times, please make sure you are using the most recent email link.');
	};


	const { mutate, isLoading, isError, error } = useMutation(
		(password) => {
			return axios.post(`${process.env.REACT_APP_API_ROOT}/V1/Auth/completeReset`, {
				password: password,
				token: resetToken
			});
		},
		{ 
			onSuccess: onResetSuccess,
			onError: onResetError  
		}
	);

	const handleSubmit = ({ password }) => {
		mutate(password);
	};

	const handlePasswordChange = (event) => {
		setPassword(event.target.value);
	};

	return (
		<Row type='flex' justify='center' align='middle'>
			<Col>
				<Card className='text-center' bodyStyle={{ paddingBottom: 10 }}>
					<Space direction='vertical'>
						<h5>Set New Password</h5>
						{!isSuccess && (
							<Space direction='vertical' size={0}>
								<Text type='secondary'>Password must include:</Text>
								<ul className='text-start mb-2'>
									{passwordRequirements.map(({ label, isMet }) => {
										const requirementIsMet = isMet(password);
										const color = getRequirementColor(password, requirementIsMet);

										return (
											<li key={label} style={{ color }}>
												<Text type={color ? undefined : 'secondary'} style={{ color }}>{label}</Text>
											</li>
										);
									})}
								</ul>
							</Space>
						)}
						{isError ? (
							<Alert
								message={error.response?.data.message ?? 'Server Error'}
								type='error'
								showIcon
								className='mb-2'
							/>
						) : null}
						{!isSuccess && <Form
							name='normal_login'
							className='justify-content-center'
							initialValues={{
								remember: true,
							}}
							onFinish={handleSubmit}
							layout='vertical'
						>
							<Form.Item
								name='password'
								label='New Password'
								rules={[
									{
										required: true,
										message: 'Password is required',
									},
									{
										validator(_, value) {
											const errors = getPasswordRequirementErrors(value);

											if (!value || errors.length === 0) {
												return Promise.resolve();
											}

											return Promise.reject(
												new Error(passwordRequirementErrorMessage)
											);
										},
									},
								]}
							>
								<Input
									onChange={handlePasswordChange}
									type='password'
								/>
							</Form.Item>
							<Form.Item
								name='confirmPassword'
								label='Confirm New Password'
								hasFeedback
								dependencies={['password']}
								rules={[
									{
										required: true,
										message: 'Please confirm your password!',
									},
									({ getFieldValue }) => ({
										validator(_, value) {
											if (!value || getFieldValue('password') === value) {
												return Promise.resolve();
											}
											return Promise.reject(
												'The two passwords that you entered do not match!'
											);
										},
									}),
								]}
							>
								<Input
									type='password'
								/>
							</Form.Item>
							<Form.Item>
								<Button
									type='primary'
									htmlType='submit'
									block
									disabled={isLoading || isSuccess}
								>
									{isLoading || isSuccess ? <Spin /> : 'Reset Password'}
								</Button>
							</Form.Item>
						</Form>}
						{isSuccess && (
							<Result
								status="success"
								title="Successfully Reset password"
								subTitle="You may close this browser tab at any time."
							/>
						)}
					</Space>
				</Card>
			</Col>
		</Row>	
	);
};

export default LoginReset;
